import { generatePath } from 'react-router-dom';

import { Box, Link, Stack, Button, Typography } from '@mui/material';

import { paths } from 'src/routes/paths';
import { useParams } from 'src/routes/hooks';
import { RouterLink } from 'src/routes/components';

import type { NewFooterProps, FooterNavigationItem } from './types';
import { ASSET_PATH, BRAND_LOGOS, DEFAULT_LINKS, SUPPORT_LINKS, PLATFORM_LINKS } from './constants';
import {
  colors,
  logosStyles,
  trimmedText,
  bottomStyles,
  footerStyles,
  columnsStyles,
  appButtonStyles,
  navigationTextStyles,
} from './styles';

function FooterNavigation({
  title,
  items,
  links,
}: {
  title: string;
  items: FooterNavigationItem[];
  links: NewFooterProps['links'];
}) {
  return (
    <Stack component="nav" aria-label={title} spacing={1.5}>
      <Typography
        component="h2"
        sx={{
          ...trimmedText,
          fontSize: 14,
          fontWeight: { xs: 700, lg: 500 },
          lineHeight: 1.2,
          pb: 0.5,
        }}
      >
        {title}
      </Typography>
      {items.map(({ id, label }) => {
        const href = links?.[id];
        const newTab = !!href && (/^(https?:)?\/\//.test(href) || /\.pdf(?:[?#]|$)/i.test(href));
        const browserLink = newTab || (!!href && /^(#|mailto:|tel:)/i.test(href));
        // Future sections stay as text until a destination is supplied.
        return href ? (
          <Link
            key={id}
            component={browserLink ? 'a' : RouterLink}
            href={href}
            target={newTab ? '_blank' : undefined}
            rel={newTab ? 'noopener noreferrer' : undefined}
            underline="hover"
            sx={navigationTextStyles}
          >
            {label}
          </Link>
        ) : (
          <Typography key={id} component="span" sx={navigationTextStyles}>
            {label}
          </Typography>
        );
      })}
    </Stack>
  );
}

export default function NewFooter({ links: customLinks }: NewFooterProps = {}) {
  const { bu, event } = useParams();
  const links = {
    ...DEFAULT_LINKS,
    ...(bu && event
      ? {
          benefits: generatePath(paths.home.root, { bu, event }),
          account: generatePath(paths.user.profile, { bu, event }),
        }
      : {}),
    ...customLinks,
  };

  return (
    <Box component="footer" aria-label="Rodapé Cidade Center Norte" sx={footerStyles}>
      <Stack spacing={{ xs: 3.5, lg: 4.5 }} sx={{ maxWidth: 1280, mx: 'auto' }}>
        <Box sx={columnsStyles}>
          <Box sx={{ minHeight: { xs: 100, lg: '167.06px' } }}>
            <Box
              component="img"
              src={`${ASSET_PATH}/cidade-center-norte.svg`}
              alt="Cidade Center Norte"
              sx={{ display: 'block' }}
            />
            <Typography
              sx={{
                ...trimmedText,
                mt: 3,
                maxWidth: 280,
                fontSize: 14,
                fontWeight: 400,
                lineHeight: 1.2,
              }}
            >
              Uma iniciativa Cidade Center Norte para transformar cada visita em uma experiência de
              valor.
            </Typography>
            <Box sx={{ display: { xs: 'none', lg: 'block' }, mt: 4 }}>
              <Typography
                fontSize={12}
                fontWeight={500}
                lineHeight={1.2}
                color="rgba(255,255,255,0.75)"
              >
                Parte do ecossistema
              </Typography>
              <Typography fontSize={12} fontWeight={700} lineHeight={1.4}>
                Cidade Center Norte
              </Typography>
            </Box>
          </Box>

          <FooterNavigation title="Plataforma" items={PLATFORM_LINKS} links={links} />
          <FooterNavigation title="Suporte" items={SUPPORT_LINKS} links={links} />

          <Stack spacing={2} sx={{ minWidth: 0 }}>
            <Typography
              component="h2"
              sx={{
                ...trimmedText,
                fontSize: 14,
                fontWeight: { xs: 700, lg: 500 },
                lineHeight: 1.2,
              }}
            >
              Baixe o App Center Norte
            </Typography>
            <Stack spacing={2.5}>
              <Typography
                sx={{
                  fontSize: { xs: 24, lg: 18 },
                  fontWeight: 700,
                  lineHeight: { xs: 1.2, lg: 1.4 },
                }}
              >
                Suas visitas geram
                <br />
                benefícios.
              </Typography>
              <Button
                component="a"
                href={links.app}
                target="_blank"
                rel="noopener noreferrer"
                variant="contained"
                sx={appButtonStyles}
              >
                Baixe o App Center Norte
              </Button>
            </Stack>
          </Stack>
        </Box>

        <Box sx={bottomStyles}>
          <Typography
            sx={{
              ...trimmedText,
              maxWidth: 620,
              minWidth: 0,
              fontSize: 12,
              fontWeight: 500,
              lineHeight: 1.2,
            }}
          >
            A Cidade Center Norte é o pólo multi-negócios composto pelos Shoppings Center Norte e
            Lar Center, o Expo Center Norte e o Novotel, numa área de 600.000 m².
          </Typography>
          <Box sx={logosStyles}>
            {BRAND_LOGOS.map(({ file, name }) => (
              <Box
                key={file}
                sx={{
                  bgcolor: colors.text,
                  borderRadius: '4px',
                  width: 84,
                  height: 76,
                  p: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box
                  component="img"
                  src={`${ASSET_PATH}/${file}`}
                  alt={name}
                  sx={{ display: 'block', flexShrink: 0 }}
                />
              </Box>
            ))}
          </Box>
        </Box>
      </Stack>
    </Box>
  );
}
